import React, { useEffect, useState } from 'react';
import { UrlParam } from 'types/Misc';
import { hex2rgba } from 'utils/hexToRgba';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SocialsType } from 'types/SocialsType';
import { isDesktop } from 'react-device-detect';
import { getCustomData } from 'utils/customData';
import { SmsOptInType } from 'types/SmsOptInTypes';
import { validateSocials } from 'utils/validateSocials';
import { showToast } from 'components/atomic/Toast/Toast';
import { isLightColor, shadeChanger } from 'utils/shadeChanger';
import { ReactComponent as OneIcon } from 'assets/icons/svg/one.svg';
import { ReactComponent as TwoIcon } from 'assets/icons/svg/two.svg';
import { ReactComponent as FourIcon } from 'assets/icons/svg/four.svg';
import { ReactComponent as FiveIcon } from 'assets/icons/svg/five.svg';
import { ReactComponent as ThreeIcon } from 'assets/icons/svg/three.svg';
import { ReactComponent as PayoutIcon } from 'assets/icons/svg/payout.svg';
import { ReactComponent as CartIcon } from 'assets/icons/svg/blue-cart.svg';
import { ReactComponent as EmailLogo } from 'assets/icons/svg/mail-colored.svg';
import { ReactComponent as ReceiptIcon } from 'assets/icons/svg/receipt-button.svg';
import { ReactComponent as SmsMessageIcon } from 'assets/icons/svg/sms-message.svg';
import { ReactComponent as Checkmark } from 'assets/icons/svg/circular-checkmark.svg';
import {
  CornerStyle,
  useThemeContext,
} from 'context/ThemeContext/ThemeContext';
import ConsentCheck from 'components/atomic/ConsentCheck';
import HtmlWrapper from 'components/wrappers/HtmlWrapper';
import SocialIcons from 'components/atomic/SocialIcons';
import useRedirectLink from 'hooks/useRedirectLink';
import TelInput from 'components/atomic/TelInput';
import Button from 'components/atomic/Button';
import Input from 'components/atomic/Input';
import RebateStatus from './RebateStatus';
import validator from 'validator';
import Cookies from 'js-cookie';
import envVars from 'env-vars';

type SmsOptInProps = {
  logo: boolean;
  data: SmsOptInType | null;
  socials: SocialsType;
  brandName: string;
  brandId: string;
  showRebateStatusPage?: boolean;
};

const getInstructionIcon = (iconKey: string, iconColor: string) => {
  switch (iconKey) {
    case 'one':
      return <OneIcon width={30} height={30} fill={iconColor} />;
    case 'two':
      return <TwoIcon width={30} height={30} fill={iconColor} />;
    case 'three':
      return <ThreeIcon width={30} height={30} fill={iconColor} />;
    case 'four':
      return <FourIcon width={30} height={30} fill={iconColor} />;
    case 'five':
      return <FiveIcon width={30} height={30} fill={iconColor} />;
    case 'payout':
      return <PayoutIcon width={30} height={30} fill={iconColor} />;
    case 'blue-cart':
      return <CartIcon width={30} height={30} fill={iconColor} />;
    case 'receipt-button':
      return <ReceiptIcon width={30} height={30} fill={iconColor} />;
    default:
      return (
        <div className='w-8 h-8 min-w-8 min-h-8 aspect-square rounded-full bg-gray-300 opacity-50' />
      );
  }
};

const SmsOptIn: React.FC<SmsOptInProps> = ({
  logo,
  data,
  socials,
  brandId,
  brandName,
  showRebateStatusPage = false,
}) => {
  const [currentScreen, setCurrentScreen] = useState<'sms' | 'email'>('sms');
  const [emailConsentChecked, toggleEmailConsentChecked] =
    useState<boolean>(false);
  const [smsConsentChecked, toggleSmsConsentChecked] = useState<boolean>(false);
  const [numberField, toggleNumberField] = useState<boolean>(isDesktop);
  const [consentError, setConsentError] = useState<boolean>(false);
  const [otpRequired, setOtpRequired] = useState<boolean>(false);
  const [otpLoading, setOtpLoading] = useState<boolean>(false);
  const [textSent, toggleTextSent] = useState<boolean>(false);
  const [rebateEmail, setRebateEmail] = useState<string>('');
  const [textNumber, setTextNumber] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [otpSent, setOtpSent] = useState<boolean>(false);
  const [otpCode, setOtpCode] = useState<string>('');
  const [showRebateStatus, setShowRebateStatus] = useState<boolean>(() =>
    window.location.search.includes('rebateId=')
  );
  const [existingPhone, setExistingPhone] = useState<string | null>(null);
  const [checkingEmail, setCheckingEmail] = useState<boolean>(false);
  const [useEmailForOtp, setUseEmailForOtp] = useState<boolean>(false);
  const redirectToExternalLink = useRedirectLink();
  const { menuStyle, brandTheme, buttonStyle, secondaryBrandColor } =
    useThemeContext();
  const { id } = useParams<UrlParam>();
  const { t } = useTranslation('translation', {
    keyPrefix: 'smsOptIn',
  });

  useEffect(() => {
    if (data?.signupMethod === 'SMS_EMAIL') setCurrentScreen('email');
    else setCurrentScreen('sms');
  }, [data?.signupMethod]);

  useEffect(() => {
    if (
      data?.optInComponent?.emailConsentBox &&
      data?.optInComponent?.emailConsentCheckedByDefault
    )
      toggleEmailConsentChecked(true);
  }, [
    data?.optInComponent?.emailConsentBox,
    data?.optInComponent?.emailConsentCheckedByDefault,
  ]);

  useEffect(() => {
    if (
      data?.optInComponent?.consentBox &&
      data?.optInComponent?.checkedAsDefault
    )
      toggleSmsConsentChecked(true);
  }, [data?.optInComponent]);

  useEffect(() => {
    if (!data?.enableTextToOptinOnMobile) toggleNumberField(true);
  }, [data?.enableTextToOptinOnMobile]);

  const defaultLegalSmsConsent = t('defaultSmsDisclaimerText')
    .replace('{{brand_name}}', brandName)
    .replace('https://brij.it/terms', `/p/${brandId}`);

  const isOtpRequired = (): boolean => {
    return (
      data?.signupMethod === 'SMS_EMAIL' &&
      rebateEmail !== '' &&
      validator.isEmail(rebateEmail)
    );
  };

  const requestOtp = async (): Promise<boolean> => {
    const API_URL =
      envVars.REACT_APP_STAND_ALONE_MODE === 'false'
        ? envVars.REACT_APP_API_URL
        : '';

    setOtpLoading(true);
    try {
      const requestBody = useEmailForOtp
        ? { email: rebateEmail }
        : { phoneNumber: textNumber.replace(/[\s()-]/g, '') };

      const response = await fetch(`${API_URL}/shared-api/otp/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });
      // const response = await fetch(
      //   `https://rc.brij.it/shared-api/otp/request`,
      //   {
      //     method: 'POST',
      //     headers: { 'Content-Type': 'application/json' },
      //     body: JSON.stringify({
      //       phoneNumber: textNumber.replace(/[\s()-]/g, ''),
      //     }),
      //   }
      // );

      if (response.ok) {
        showToast({
          message: t('otpSentSuccess'),
          type: 'success',
        });
        setOtpSent(true);
        return true;
      } else {
        showToast({
          message: t('otpSendError'),
          type: 'error',
        });
        return false;
      }
    } catch (error) {
      showToast({
        message: t('otpSendError'),
        type: 'error',
      });
      console.error('Error requesting OTP:', error);
      return false;
    } finally {
      setOtpLoading(false);
    }
  };

  const verifyOtp = async (): Promise<boolean> => {
    if (otpCode.length !== 6) {
      showToast({
        message: t('otpInvalidLength'),
        type: 'error',
      });
      return false;
    }

    const API_URL =
      envVars.REACT_APP_STAND_ALONE_MODE === 'false'
        ? envVars.REACT_APP_API_URL
        : '';

    setOtpLoading(true);
    try {
      const requestBody = useEmailForOtp
        ? { email: rebateEmail, code: otpCode }
        : {
            phoneNumber: textNumber.replace(/[\s()-]/g, ''),
            code: otpCode,
            email: rebateEmail,
          };

      const response = await fetch(`${API_URL}/shared-api/otp/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });
      // const response = await fetch(`https://rc.brij.it/shared-api/otp/verify`, {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({
      //     phoneNumber: textNumber.replace(/[\s()-]/g, ''),
      //     code: otpCode,
      //   }),
      // });

      if (response.ok) {
        const responseData = await response.json();
        if (responseData.success === false) {
          const errorMessage =
            responseData.message || responseData.error || t('otpVerifyError');
          showToast({
            message: errorMessage,
            type: 'error',
          });
          return false;
        }
        return true;
      } else {
        showToast({
          message: t('otpVerifyError'),
          type: 'error',
        });
        return false;
      }
    } catch (error) {
      showToast({
        message: t('otpVerifyError'),
        type: 'error',
      });
      console.error('Error verifying OTP:', error);
      return false;
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setOtpCode('');
    await requestOtp();
  };

  // TEXT US
  const openSMSApp = async () => {
    if (
      data?.optInComponent?.consentBox &&
      data?.optInComponent?.consentRequired &&
      !smsConsentChecked
    ) {
      setConsentError(true);
      showToast({
        message: t('consentRequiredError'),
        type: 'error',
      });
      return;
    } else setConsentError(false);

    const API_URL =
      envVars.REACT_APP_STAND_ALONE_MODE === 'false'
        ? envVars.REACT_APP_API_URL
        : '';

    setLoading(true);
    const rebateId: string = Cookies.get(`rebateId_${id}`) || '';

    if (rebateId) {
      const url = `${API_URL}/app_api/rebate/${rebateId}/redirect`;

      const customDataObject = getCustomData();
      const hasCustomData = Object.keys(customDataObject).length > 0;
      const customData = hasCustomData
        ? Object.entries(customDataObject).map(([key, value]) => ({
            key,
            value,
          }))
        : [];

      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            consent: {
              consentType: data?.optInComponent?.consentBox
                ? 'explicit'
                : 'implicit',
              consentFor: 'sms',
              defaultLanguage: defaultLegalSmsConsent,
              language: data?.customLegalConsent
                ? data?.customLegalText
                : t('defaultSmsDisclaimerText'),
              allowed: data?.optInComponent?.consentBox
                ? smsConsentChecked
                : true,
            },
            ...(customData && { customData }),
          }),
        });

        if (!response.ok) {
          console.error('Error:', await response.json());
          showToast({
            message: t('errorSendingRequest'),
            type: 'error',
          });
        } else {
          const data = await response.json();
          redirectToExternalLink(data.redirectUrl, '_self');
        }
      } catch (error) {
        showToast({
          message: t('errorSendingRequest'),
          type: 'error',
        });
        console.error('Error:', error);
      }
    } else showToast({ message: t('rebateIdError'), type: 'error' });
    setLoading(false);
  };

  const validatePhoneAndConsent = (): boolean => {
    // Skip phone validation if using email for OTP
    if (!useEmailForOtp && textNumber.replace(/[\s()_-]/g, '').length !== 12) {
      showToast({
        message: t('invalidPhoneNumberError'),
        type: 'error',
      });
      return false;
    }
    if (
      data?.optInComponent?.consentBox &&
      data?.optInComponent?.consentRequired &&
      !smsConsentChecked
    ) {
      setConsentError(true);
      showToast({
        message: t('consentRequiredError'),
        type: 'error',
      });
      return false;
    } else {
      setConsentError(false);
    }
    return true;
  };

  // TEXT ME - Main handler for phone submit button
  const handlePhoneSubmit = async () => {
    if (!validatePhoneAndConsent()) return;

    if (isOtpRequired() && !otpSent) {
      const success = await requestOtp();
      if (success) setOtpRequired(true);
      return;
    }
    await sendOptInRequest();
  };

  const handleOtpSubmit = async () => {
    const verified = await verifyOtp();
    if (verified) {
      await sendOptInRequest();
    }
  };

  // TEXT ME
  const sendOptInRequest = async () => {
    const API_URL =
      envVars.REACT_APP_STAND_ALONE_MODE === 'false'
        ? envVars.REACT_APP_API_URL
        : '';

    setLoading(true);
    const rebateId: string = Cookies.get(`rebateId_${id}`) || '';
    if (rebateId) {
      const url = `${API_URL}/app_api/rebate/${rebateId}/text`;

      const customDataObject = getCustomData();
      const hasCustomData = Object.keys(customDataObject).length > 0;
      const customData = hasCustomData
        ? Object.entries(customDataObject).map(([key, value]) => ({
            key,
            value,
          }))
        : [];

      const body = {
        phoneNumber: textNumber.replace(/[\s()-]/g, ''),
        consent: {
          consentType: data?.optInComponent?.consentBox
            ? 'explicit'
            : 'implicit',
          consentFor: 'sms',
          defaultLanguage: defaultLegalSmsConsent,
          language: data?.customLegalConsent
            ? data?.customLegalText
            : t('defaultSmsDisclaimerText'),
          allowed: data?.optInComponent?.consentBox ? smsConsentChecked : true,
        },
        email: rebateEmail || '',
        ...(customData && { customData }),
      };
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (response?.status === 409) {
          showToast({
            message: t('rebateAlreadyClaimedError'),
            type: 'error',
          });
        } else if (!response.ok) {
          let errorMessage = t('errorSendingRequest');
          let shouldShowConflictError = false;

          try {
            const errorData = await response.json();
            console.error('Error:', errorData);

            const rawErrorMessage =
              errorData?.error || errorData?.message || '';

            const cleanedMessage = rawErrorMessage
              ? rawErrorMessage.replace(/^Error sending text:\s*/i, '').trim()
              : '';

            errorMessage = cleanedMessage || t('errorSendingRequest');

            if (errorData?.conflict) {
              shouldShowConflictError = true;
            }
          } catch (parseError) {
            console.error('Error parsing response:', parseError);
          }

          if (shouldShowConflictError) {
            showToast({
              message: t('rebateInProgressConflictError'),
              type: 'error',
            });
          } else {
            showToast({
              message: errorMessage,
              type: 'error',
            });
          }
        } else {
          showToast({
            message: t('messageSentSuccessfully'),
            type: 'success',
          });
          if (showRebateStatusPage) {
            window.history.pushState(
              {},
              '',
              `${window.location.pathname}?rebateId=${rebateId}`
            );
            setShowRebateStatus(true);
          }
          toggleTextSent(true);
        }
      } catch (error) {
        showToast({
          message: t('errorSendingRequest'),
          type: 'error',
        });
        console.error('Error:', error);
      }
    } else showToast({ message: t('rebateIdError'), type: 'error' });
    setLoading(false);
  };

  const handleRebateEmailSubmission = async (emailSkipped: boolean = false) => {
    if (emailSkipped) {
      setCurrentScreen('sms');
      return;
    }

    if (
      data?.optInComponent?.emailConsentBox &&
      data?.optInComponent?.emailConsentRequired &&
      !emailConsentChecked
    ) {
      setConsentError(true);
      return;
    }

    if (!validateEmail(rebateEmail)) return;

    // Check if email exists with phone number
    const API_URL =
      envVars.REACT_APP_STAND_ALONE_MODE === 'false'
        ? envVars.REACT_APP_API_URL
        : '';

    setCheckingEmail(true);
    try {
      const response = await fetch(
        `${API_URL}/shared-api/users/check-rebates-email?email=${encodeURIComponent(
          rebateEmail
        )}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.exists && data.truncatedPhone) {
          // Email exists with phone number
          setExistingPhone(data.truncatedPhone);
          setUseEmailForOtp(true);
        } else {
          // Email doesn't exist or no phone number
          setExistingPhone(null);
          setUseEmailForOtp(false);
        }
      }
    } catch (error) {
      console.error('Error checking email:', error);
      // Continue with normal flow on error
      setExistingPhone(null);
      setUseEmailForOtp(false);
    } finally {
      setCheckingEmail(false);
      setCurrentScreen('sms');
    }
  };

  const validateEmail = (value: string) => {
    if (!value) {
      showToast({
        message: t('emailInputError'),
        type: 'error',
      });
      return false;
    } else {
      if (validator.isEmail(value) && !value.toLowerCase().endsWith('.con')) {
        setConsentError(false);
        return true;
      } else {
        showToast({
          message: t('invalidEmailError'),
          type: 'error',
        });
        return false;
      }
    }
  };

  const formatPhoneNumber = (phoneNumber: string): string => {
    const cleaned = phoneNumber.replace(/\D/g, '');
    if (cleaned.length >= 4) {
      const lastFour = cleaned.slice(-4);
      return `(***) ***-${lastFour}`;
    }
    return phoneNumber;
  };

  const getCornerStyle = (isMenuStyle: boolean, styleProperty: string) => {
    switch (styleProperty) {
      case CornerStyle.FULL_ROUND:
        return isMenuStyle ? 'rounded-3xl' : 'rounded-full';
      case CornerStyle.ROUNDED_CORNERS:
        return 'rounded-sm1';
      case CornerStyle.SQUARE_CORNERS:
        return 'rounded-none';
      default:
        return isMenuStyle ? 'rounded-3xl' : 'rounded-full';
    }
  };

  return (
    <>
      {data?.dimBackground && (
        <div className='absolute top-0 left-0 w-full h-full bg-black/50 duration-300' />
      )}
      <div
        className={`absolute top-0 left-0 w-full h-full flex flex-col justify-between items-stretch ${
          logo ? 'max-h-[calc(100%-5.25rem)] mt-[5.25rem]' : 'max-h-full mt-0'
        }`}
      >
        <div
          className={`relative w-full h-full px-5 overflow-auto 
            ${
              data?.socialMediaIcons
                ? 'max-h-[calc(100%-5.25rem)] pb-5'
                : 'max-h-full pb-6'
            } 
            ${logo ? 'pt-0' : 'pt-4'}
          `}
        >
          {showRebateStatus ? (
            <RebateStatus />
          ) : (
            <div
              className={`
              ${
                data?.optInComponent?.showBackground
                  ? 'py-4 px-6 shadow-md'
                  : 'p-4 shadow-none'
              }
              ${getCornerStyle(true, menuStyle)}
            `}
              style={{
                background: data?.optInComponent?.showBackground
                  ? data?.optInComponent?.backgroundColor || '#FFFFFF'
                  : 'transparent',
              }}
            >
              {data?.instructions?.showSteps && (
                <div className='w-full relative flex flex-col items-center justify-center mt-2 mb-3 rounded-[14px] border border-solid border-primary overflow-hidden'>
                  <div className='w-full relative flex items-center justify-center px-4 py-2 bg-primary'>
                    <p className='font-semibold text-white text-lg'>
                      {data?.instructions.title}
                    </p>
                  </div>
                  <ul className='relative w-full flex flex-col items-start justify-start px-4 py-3 gap-3'>
                    {data?.instructions?.messages.map((message, index) => (
                      <li
                        key={`${message?.title}-${index}`}
                        className='w-full flex flex-row items-center justify-start gap-2'
                      >
                        <div className='flex items-center justify-center w-instructionIcon h-instructionIcon min-w-instructionIcon min-h-instructionIcon rounded-full overflow-hidden bg-primary'>
                          {getInstructionIcon(
                            message?.icon,
                            secondaryBrandColor || '#FFFFFF'
                          )}
                        </div>
                        <p className='font-medium text-darkGray text-xs'>
                          {message?.title}
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <HtmlWrapper
                html={data?.optInComponent?.content}
                background='transparent'
                styles='!mb-2'
              />
              {currentScreen === 'sms' ? (
                <>
                  {numberField && textSent && (
                    <div
                      className={`flex flex-row items-center justify-start px-3 py-1.5 gap-3 mb-1.5 ${getCornerStyle(
                        false,
                        buttonStyle
                      )}`}
                      style={{ background: hex2rgba(brandTheme, 0.1) }}
                    >
                      <Checkmark />
                      <p className='font-medium text-xxs text-primary [&_span]:underline [&_span]:cursor-pointer'>
                        {t('messageSentText')}
                        <span onClick={() => window.location.reload()}>
                          {t('trySendingAgain')}
                        </span>
                        .
                      </p>
                    </div>
                  )}
                  {!textSent &&
                    !otpRequired &&
                    data?.communicationChannel !== 'SMS' && (
                      <div className='relative w-full flex flex-col items-center justify-center mt-2 mb-3 gap-2'>
                        <p className='text-sm font-semibold text-center'>
                          {t('verifyPhoneNumberTitle')}
                        </p>
                        <p className='font-light text-xs text-center'>
                          {t('verifyPhoneNumberDescription')}
                        </p>
                      </div>
                    )}
                  {otpRequired && otpSent ? (
                    <div
                      className={`relative flex flex-col items-center justify-center text-center gap-2.5 mt-3 ${
                        isDesktop ? '!mb-1' : '!mb-2'
                      }`}
                    >
                      <h4
                        className={`text-sm font-semibold text-center ${
                          isLightColor(
                            data?.optInComponent?.backgroundColor || '#FFFFFF',
                            130
                          )
                            ? '!text-black'
                            : '!text-white/90'
                        }`}
                      >
                        {t('otpConfirmPhoneHeading')}
                      </h4>
                      <p
                        className={`font-light text-xs text-center ${
                          isLightColor(
                            data?.optInComponent?.backgroundColor || '#FFFFFF',
                            130
                          )
                            ? '!text-black'
                            : '!text-white/90'
                        }`}
                      >
                        {t('otpPasscodeSentMessage').replace(
                          '{{phoneNumber}}',
                          existingPhone || formatPhoneNumber(textNumber)
                        )}
                      </p>
                    </div>
                  ) : (
                    data?.optInComponent?.consentBox &&
                    !textSent && (
                      <ConsentCheck
                        styles={isDesktop ? '!mb-1' : `!mb-2`}
                        enabled={[true]}
                        errors={[consentError]}
                        consentChecks={[smsConsentChecked]}
                        toggleConsentChecks={[toggleSmsConsentChecked]}
                        texts={[
                          data?.customLegalConsent
                            ? data?.customLegalText
                            : defaultLegalSmsConsent,
                        ]}
                      />
                    )
                  )}
                  <div
                    className={`w-full px-0.5 duration-200 overflow-hidden ${
                      numberField ? 'h-0' : 'h-[58px]'
                    }`}
                  >
                    <Button
                      variant='dark'
                      styles='!text-sm !px-12 !mt-1 !duration-200'
                      title={
                        data?.enableTextToOptinOnMobile
                          ? data?.buttonCTALinkToMessagingAppText
                          : t('sendMessageButton')
                      }
                      onClick={() => openSMSApp()}
                      icon={
                        <SmsMessageIcon
                          width={32}
                          fill={brandTheme}
                          className='absolute left-3.5'
                        />
                      }
                    />
                  </div>
                  <div
                    className={`
                    relative w-full flex flex-col px-0.5 overflow-hidden 
                    ${textSent ? 'duration-0' : 'duration-200'} 
                    ${
                      numberField
                        ? `${
                            isDesktop
                              ? `pt-1 ${
                                  textSent
                                    ? 'h-0'
                                    : otpRequired && otpSent
                                    ? 'h-[180px]'
                                    : 'h-[140px]'
                                }`
                              : `${
                                  textSent
                                    ? 'h-0'
                                    : otpRequired && otpSent
                                    ? 'h-[180px]'
                                    : `${
                                        data?.signupMethod === 'SMS' &&
                                        !isDesktop
                                          ? 'h-[172px]'
                                          : 'h-[132px]'
                                      }`
                                }`
                          }`
                        : 'h-[40px]'
                    }`}
                  >
                    {numberField ? (
                      <>
                        {!textSent && (
                          <>
                            {otpRequired && otpSent ? (
                              <>
                                <Input
                                  autoFocus
                                  type='number'
                                  styles='!mt-1 !bg-white'
                                  name='otp-input'
                                  value={otpCode}
                                  placeholder={
                                    otpCode === ''
                                      ? t('otpInputPlaceholder')
                                      : t('otpInputLabel')
                                  }
                                  onChange={(event) => {
                                    const value = event.target.value.slice(
                                      0,
                                      6
                                    );
                                    setOtpCode(value);
                                  }}
                                />
                                <Button
                                  variant='dark'
                                  loading={otpLoading || loading}
                                  styles='!text-sm !px-12 !mt-3 !duration-200'
                                  onClick={() => handleOtpSubmit()}
                                  title={t('otpVerifyButton')}
                                  icon={
                                    <SmsMessageIcon
                                      width={32}
                                      fill={brandTheme}
                                      className='absolute left-3.5'
                                    />
                                  }
                                />
                                <Button
                                  variant='link'
                                  disabled={otpLoading}
                                  title={t('otpResendButton')}
                                  onClick={() => handleResendOtp()}
                                  styles={`!text-sm !h-10 !mt-3 !mb-1 ${
                                    isLightColor(
                                      data?.optInComponent?.backgroundColor ||
                                        '#FFFFFF',
                                      130
                                    )
                                      ? '!text-black'
                                      : '!text-white/90'
                                  }`}
                                />
                              </>
                            ) : existingPhone ? (
                              <>
                                <div
                                  className={`relative flex flex-col items-center justify-center text-center gap-2.5 mt-3 ${
                                    isDesktop ? '!mb-1' : '!mb-2'
                                  }`}
                                >
                                  <p
                                    className={`font-medium text-sm text-center ${
                                      isLightColor(
                                        data?.optInComponent?.backgroundColor ||
                                          '#FFFFFF',
                                        130
                                      )
                                        ? '!text-black'
                                        : '!text-white/90'
                                    }`}
                                  >
                                    {t('existingPhoneMessage') + existingPhone}
                                  </p>
                                </div>
                                <Button
                                  variant='dark'
                                  loading={loading || otpLoading}
                                  styles='!text-sm !px-12 !mt-3 !duration-200'
                                  onClick={() => handlePhoneSubmit()}
                                  title={
                                    // t('receiveCodeButton') || 'Receive Code'
                                    data?.optInComponent?.customButtonCTA
                                      ? data?.optInComponent
                                          ?.customButtonCTAPhoneNumberSubmissionText
                                      : t('receiveMessageButton')
                                  }
                                  icon={
                                    <SmsMessageIcon
                                      width={32}
                                      fill={brandTheme}
                                      className='absolute left-3.5'
                                    />
                                  }
                                />
                              </>
                            ) : (
                              <>
                                <TelInput
                                  autoFocus={true}
                                  value={textNumber}
                                  mask='(+1) 999 999 9999'
                                  name='phone-number-input'
                                  styles='!h-input !flex-shrink-0 mt-1'
                                  placeholder={t('phoneNumberInputPlaceholder')}
                                  onChange={(event) =>
                                    setTextNumber(event.target.value)
                                  }
                                />
                                <Button
                                  variant='dark'
                                  loading={loading || otpLoading}
                                  styles='!text-sm !px-12 !mt-3 !duration-200'
                                  onClick={() => handlePhoneSubmit()}
                                  title={
                                    data?.optInComponent?.customButtonCTA
                                      ? data?.optInComponent
                                          ?.customButtonCTAPhoneNumberSubmissionText
                                      : t('receiveMessageButton')
                                  }
                                  icon={
                                    <SmsMessageIcon
                                      width={32}
                                      fill={brandTheme}
                                      className='absolute left-3.5'
                                    />
                                  }
                                />
                                {data?.signupMethod === 'SMS' && !isDesktop ? (
                                  <Button
                                    variant='link'
                                    title={t('sendUsText')}
                                    onClick={() => toggleNumberField(false)}
                                    styles={`!text-sm !h-8 mt-2 ${
                                      isLightColor(
                                        data?.optInComponent?.backgroundColor ||
                                          '#FFFFFF',
                                        130
                                      )
                                        ? '!text-black'
                                        : '!text-white/90'
                                    }`}
                                  />
                                ) : null}
                              </>
                            )}
                          </>
                        )}
                      </>
                    ) : (
                      <Button
                        variant='link'
                        title={t('sendMeText')}
                        onClick={() => toggleNumberField(true)}
                        styles={`!text-sm !h-8 mt-0.5 ${
                          isLightColor(
                            data?.optInComponent?.backgroundColor || '#FFFFFF',
                            130
                          )
                            ? '!text-black'
                            : '!text-white/90'
                        }`}
                      />
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div>
                    {data?.optInComponent?.emailConsentBox && (
                      <ConsentCheck
                        styles={isDesktop ? '!mb-1' : `!mb-2`}
                        enabled={[true]}
                        errors={[consentError]}
                        consentChecks={[emailConsentChecked]}
                        texts={[
                          data?.optInComponent?.emailConsentBoxText &&
                          data?.optInComponent?.customizeEmailConsentText
                            ? data?.optInComponent?.emailConsentBoxText
                            : t('defaultEmailLegalText')
                                .replace('{{brand_name}}', brandName)
                                .replace(
                                  'https://brij.it/terms',
                                  `/p/${brandId}`
                                ),
                        ]}
                        toggleConsentChecks={[toggleEmailConsentChecked]}
                      />
                    )}
                    <Input
                      autoFocus
                      type='email'
                      name='reabte-email'
                      value={rebateEmail}
                      autoComplete='email'
                      className='rebate-email-input'
                      aria-label={t('ariaRebateEmailInput')}
                      styles='!h-input !flex-shrink-0 !mt-3 !bg-white'
                      onChange={(event) => setRebateEmail(event.target.value)}
                      placeholder={
                        rebateEmail === ''
                          ? t('rebateEmailInputPlaceholder')
                          : t('rebateEmailInputFilledPlaceholder')
                      }
                    />
                    <Button
                      variant='dark'
                      loading={loading || checkingEmail}
                      title={t('submitButtonText')}
                      styles='!text-sm !px-12 !my-3 !duration-200'
                      onClick={() => handleRebateEmailSubmission(false)}
                      icon={
                        <EmailLogo
                          width={32}
                          fill='#FFFFFF'
                          className='absolute left-3.5'
                        />
                      }
                    />
                    {data?.communicationChannel === 'SMS' &&
                      (loading ? (
                        <div className='relative w-full !h-8 !mb-3 bg-transparent' />
                      ) : (
                        <Button
                          variant='link'
                          title={t('skipButtonText')}
                          onClick={() => handleRebateEmailSubmission(true)}
                          styles={`!text-sm !h-8 !mb-3 ${
                            isLightColor(
                              data?.optInComponent?.backgroundColor ||
                                '#FFFFFF',
                              130
                            )
                              ? '!text-black'
                              : '!text-white/90'
                          }`}
                        />
                      ))}
                  </div>
                </>
              )}
              {currentScreen === 'email' &&
                !data?.optInComponent?.emailConsentBox && (
                  <HtmlWrapper
                    html={
                      data?.optInComponent?.emailConsentBoxText &&
                      data?.optInComponent?.customizeEmailConsentText
                        ? data?.optInComponent?.emailConsentBoxText
                        : t('defaultEmailLegalText')
                            .replace('{{brand_name}}', brandName)
                            .replace('https://brij.it/terms', `/p/${brandId}`)
                    }
                    background='transparent'
                    styles='px-2.5 [&_p]:!text-xxxs [&_p]:!leading-[12px] text-justify [&_p]:!opacity-50'
                    paragraphTextColor={
                      isLightColor(
                        data?.optInComponent?.backgroundColor || '#FFFFFF',
                        130
                      )
                        ? shadeChanger('#000000', 50)
                        : shadeChanger('#FFFFFF', 30)
                    }
                  />
                )}
              {currentScreen === 'sms' && !data?.optInComponent?.consentBox && (
                <HtmlWrapper
                  background='transparent'
                  html={
                    data?.customLegalConsent
                      ? data?.customLegalText
                      : defaultLegalSmsConsent
                  }
                  styles='px-2.5 [&_p]:!text-xxxs [&_p]:!leading-[12px] text-justify [&_p]:!opacity-50'
                  paragraphTextColor={
                    isLightColor(
                      data?.optInComponent?.backgroundColor || '#FFFFFF',
                      130
                    )
                      ? shadeChanger('#000000', 50)
                      : shadeChanger('#FFFFFF', 30)
                  }
                />
              )}
            </div>
          )}
        </div>
        {data?.socialMediaIcons && validateSocials(socials) && (
          <div className='relative w-full h-[5.688rem] flex items-center bg-transparent border-solid border-0 border-t border-white/20'>
            <SocialIcons socials={socials} darkIcons />
          </div>
        )}
      </div>
    </>
  );
};

export default SmsOptIn;
